import React, {useState, useEffect, useCallback, useRef} from 'react';
import { useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import HelpIcon from './HelpIcon';
import config from '../../config/config';
import chtypes from '../../helpers/chouseenumerated.js';
import './CompanyDirectorDetails.css';

const CompanyDirectorDetails = ({ value = [], onChange = () => {}, company_number, helptext }) => {
    const [directors, setDirectors] = useState([]);
    const [companynumber, setCompanyNumber] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const authToken = useSelector((state) => state.auth.token);
    const token = authToken || config.EXTERNAL_API_TOKEN;
    const prevDirectors = useRef();

    const fetchDirectors = useCallback(async () => {
        try {
            const url = `${config.API_BASE_URL}/companies/getcompanyofficers/${company_number}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
            });

            if (response.ok) {
                const cdir = await response.json();
                if (cdir.status) {
                    let dirs = [];
                    const now = new Date();
                    const prev3years = `${now.getFullYear()}-01-01`;
                    let id = 1;
                    for (const director of cdir.data.items) {
                        if (!(director.resigned_on ?? null) || (director.resigned_on > prev3years)) {
                            dirs.push({
                                name: formatName(director.name),
                                from: director.appointed_on,
                                to: director.resigned_on ?? null,
                                officer_role: chtypes.officer_role[director.officer_role],
                                occupation: director.occupation ?? '',
                                id: director.person_number ?? id,
                                salary: 0,
                                companyhouseadded: true
                            });
                        }
                        id++;
                    }
                    setDirectors(dirs);
                    setCompanyNumber(company_number);
                } else {
                    setError(`Error fetching Directors Details from Companies house`);
                }
            } else {
                setError(`Error fetching Directors Details from Companies house`);
            }
        } catch (err) {
            setError(`Error fetching Directors Details from Companies house (${err.toString})`);
        }
    }, [company_number, token]);

    useEffect(() => {
        async function getDirectors() {
            setLoading(true);
            setLoaded(false);
            setError("");
            try {
                await fetchDirectors();
            } catch (error) {
                setError(`Error fetching Directors Details from Companies house ${error.toString()}`);
            }
            setLoading(false);
            setLoaded(true);
        }
        if (company_number && companynumber !== company_number)
            getDirectors();
    }, [loaded, company_number, companynumber, setCompanyNumber, setLoaded, fetchDirectors]);

    const formatName = (namein) => {
        let nparts = namein.split(',');
        nparts = nparts.map(item => item.trim());
        nparts = (nparts.reverse()).join(' ').toUpperCase();
        return nparts;
    }

    // Update form state whenever local directors array changes
    useEffect(() => {
        if (!isEqual(prevDirectors.current, directors)) {
            prevDirectors.current = directors;
            onChange(directors);
        }
    }, [directors, onChange]);

    useEffect(() => {
        // Only update local state if value changed from outside (not just from our own onChange)
        if (!isEqual(value, prevDirectors.current)) {
            prevDirectors.current = value;
            setDirectors(value || []);
        }
    }, [value]);

    // Update any of the Director fields
    const updateDirectorField = (e, field, director) => {
        setDirectors((curdir) => curdir.map(item => {
            if (item.id === director.id) {
                const update = {[field]: e.target.value};
                return {...director, ...update};
            } else {
                return item;
            }
        }));
    }

    const addDirector = () => {
        setDirectors((current) => {
            const newDirs = [...current, {
                name: '',
                from: null,
                to: null,
                officer_role: '',
                occupation: '',
                id: `added_${current.length + 1}`,
                salary: 0,
                companyhouseadded: false
            }];
            return newDirs;
        });
    }

    const removeDirector = (id) => {
        if (window.confirm("Are you sure you want to Delete this Director? Press OK to Confirm."))
            setDirectors((current) => current.filter(item => item.id !== id));
    }

    if (directors.length === 0)
        return null;

    return (
        <fieldset>
            <legend>
                Company Directors (active and past within the 3 Years)
                <HelpIcon helptext={helptext} />
            </legend>
            {loading && <h5>Loading Director Data. Please wait...</h5>}
            {error > "" && <span style={{color: 'red'}}>{error}</span>}
            {loaded &&
            <div className='itemcontainer'>
                <div className='itemname itemhead'>Name</div>
                <div className='itemfunction itemhead'>Function Within Company</div>
                <div className='itemfrom itemhead'>Period From</div>
                <div className='itemto itemhead'>Period To</div>
                <div className='itemsalary itemhead'>Salary Received</div>

                {directors.map((director, index) => {
                    return (
                        <React.Fragment key={director.id}>
                            <div className='itemname'>
                                <span className='label directorlabel'>Name</span>
                                <input type='text' className='smallcss' name='name' value={director.name ?? ''} onChange={(e) => updateDirectorField(e, 'name', director)} />
                            </div>
                            <div className='itemfunction'>
                                <span className='directorlabel label'>Function</span>
                                <input type='text' name='function' value={director.officer_role ?? ''} onChange={(e) => updateDirectorField(e, 'officer_role', director)} />
                            </div>
                            <div className='itemfrom'>
                                <span className='directorlabel label'>Period From</span>
                                <input type='date' name='from' value={director.from ?? ''} onChange={(e) => updateDirectorField(e, 'from', director)} />
                            </div>
                            <div className='itemto'>
                                <span className='directorlabel label'>Period To</span>
                                <input type='date' name='to' value={director.to ?? ''} onChange={(e) => updateDirectorField(e, 'to', director)} />
                            </div>
                            <div className='itemsalary'>
                                <span className='directorlabel label'>Salary Received</span>
                                <input type='number' name='salary' value={director.salary ?? ''} onChange={(e) => updateDirectorField(e, 'salary', director)} />
                            </div>
                            {!director.companyhouseadded &&
                                <i className="fa fa-trash-o" onClick={() => removeDirector(director.id)} style={{fontSize: '48px', color: 'red', marginTop: 'auto', marginBottom: 'auto'}}></i>
                            }
                            {director.companyhouseadded &&
                                <i className="fa fa-trash-o" style={{fontSize: '48px', color: 'white', marginTop: 'auto', marginBottom: 'auto'}}></i>
                            }
                        </React.Fragment>
                    );
                })}
                <button type='button' onClick={() => addDirector()}>Add Director</button>
            </div>
            }
        </fieldset>
    );
}

export default CompanyDirectorDetails;
